import { Scenario, Variable, getRiskConsequencesName, getRiskEventsName, getRiskSourcesName } from 'src/api/types';
import { formatDate, sanitizeForCSV } from 'src/utils/misc';
import { ScenarioCsvHeaders } from 'src/api/types/misc';
import { AssessmentRiskRegisterQuery } from 'src/graphql/_generated';
import { getIncompleteStateNew } from 'src/common';
import { auditEvent } from 'src/api/other';
import { gaEventEXPORT } from 'src/utils/gaEvent';
import Papa from 'papaparse';
import { formulaIdsToNames } from 'src/utils/formula';

type AssessmentScenario = NonNullable<AssessmentRiskRegisterQuery['assessment']>['scenarios'][number];

/**
 * Converts a formula to an exportable format for the formula column.
 * If formula contains variable references (#ID), converts them to variable names.
 * Returns empty string if no formula or if formula is just a plain number.
 */
const getExportableFormula = (formula: string | null | undefined, variables: Variable[]): string => {
  // If there's a formula with variable references, convert IDs to names
  if (formula && formula.includes('#')) {
    return formulaIdsToNames(formula, variables);
  }
  // No formula or plain number - return empty string (value will be in the value column)
  return '';
};

const getCSVData = (scenarios: AssessmentScenario[], variables: Variable[]) => {
  const headers = [
    ScenarioCsvHeaders.Name,
    ScenarioCsvHeaders.ID,
    ScenarioCsvHeaders.Status,
    ScenarioCsvHeaders.Sources,
    ScenarioCsvHeaders.Events,
    ScenarioCsvHeaders.Consequences,
    ScenarioCsvHeaders.RiskOwner,
    ScenarioCsvHeaders.RiskNarrative,
    'Unmodified Expected Loss',
    'Live Expected Loss',
    'Projected Expected Loss',
    ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
    ScenarioCsvHeaders.UnmodifiedFrequencyYears,
    ScenarioCsvHeaders.LiveFrequencyTimes,
    ScenarioCsvHeaders.LiveFrequencyYears,
    ScenarioCsvHeaders.ProjectedFrequencyTimes,
    ScenarioCsvHeaders.ProjectedFrequencyYears,
    ScenarioCsvHeaders.FrequencyNote,
    ScenarioCsvHeaders.UnmodifiedConsequenceLower,
    ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
    ScenarioCsvHeaders.ConsequenceLowerFormula,
    ScenarioCsvHeaders.ConsequenceUpperFormula,
    ScenarioCsvHeaders.LiveConsequenceLower,
    ScenarioCsvHeaders.LiveConsequenceUpper,
    ScenarioCsvHeaders.ProjectedConsequenceLower,
    ScenarioCsvHeaders.ProjectedConsequenceUpper,
    ScenarioCsvHeaders.ConsequenceNote,
    ScenarioCsvHeaders.Created,
    ScenarioCsvHeaders.Updated,
    ScenarioCsvHeaders.IdentifiedDate,
    ScenarioCsvHeaders.ReviewDate,
    ScenarioCsvHeaders.RiskManagementStrategy,
    ScenarioCsvHeaders.RiskManagementNote,
  ];

  return [
    headers,
    ...scenarios.map((el) => {
      const liveItem = el.liveRisk;
      const projectedItem = el.projectedRisk;
      const isIncompletetech = getIncompleteStateNew(el) === 'TECH';

      return [
        sanitizeForCSV(el.name), // Name
        sanitizeForCSV(el.id), // ID
        sanitizeForCSV(el.status), // Status
        sanitizeForCSV(getRiskSourcesName(el.source)), // Sources
        sanitizeForCSV(getRiskEventsName(el.event)), // Events
        sanitizeForCSV(getRiskConsequencesName(el.consequence)), // Consequences
        sanitizeForCSV(el.riskOwner?.text), // Risk Owner
        sanitizeForCSV(el.riskNarrative), // Risk Narrative
        isIncompletetech ? '' : Math.round(el.baseRisk?.expectedLoss || 0), // Unmodified Expected Loss
        isIncompletetech ? '' : liveItem ? Math.round(liveItem.expectedLoss || 0) : '', // Live Expected Loss
        isIncompletetech ? '' : projectedItem ? Math.round(projectedItem.expectedLoss || 0) : '', // Projected Expected Loss
        sanitizeForCSV(el.baseRisk?.frequencyTimes), // Unmodified Frequency Times
        sanitizeForCSV(el.baseRisk?.frequencyYears), // Unmodified Frequency Years
        isIncompletetech ? '' : sanitizeForCSV(liveItem?.frequencyTimes), // Live Frequency Times
        isIncompletetech ? '' : sanitizeForCSV(liveItem?.frequencyYears), // Live Frequency Years
        isIncompletetech ? '' : sanitizeForCSV(projectedItem?.frequencyTimes), // Projected Frequency Times
        isIncompletetech ? '' : sanitizeForCSV(projectedItem?.frequencyYears), // Projected Frequency Years
        sanitizeForCSV(el.frequencyNote), // Frequency Note
        // Export calculated values in value columns
        isIncompletetech ? '' : Math.round(el.baseRisk?.lower || 0), // Unmodified Consequence Lower
        isIncompletetech ? '' : Math.round(el.baseRisk?.upper || 0), // Unmodified Consequence Upper
        // Export formulas with variable names in formula columns for round-trip support
        getExportableFormula(el.lowerFormula, variables), // Unmodified Consequence Lower Formula
        getExportableFormula(el.upperFormula, variables), // Unmodified Consequence Upper Formula
        isIncompletetech ? '' : liveItem ? Math.round(liveItem?.lower || 0) : '', // Live Consequence Lower
        isIncompletetech ? '' : liveItem ? Math.round(liveItem.upper || 0) : '', // Live Consequence Upper
        isIncompletetech ? '' : projectedItem ? Math.round(projectedItem.lower || 0) : '', // Projected Consequence Lower
        isIncompletetech ? '' : projectedItem ? Math.round(projectedItem.upper || 0) : '', // Projected Consequence Upper
        sanitizeForCSV(el.consequenceNote), // Consequence Note
        formatDate(el.meta?.createdAt, { formatType: 'datetime' }), // Created
        formatDate(el.meta?.updatedAt, { formatType: 'datetime' }), // Updated
        formatDate(el.riskIdentifiedDate), // Identified Date
        formatDate(el.reviewDate), // Review Date
        sanitizeForCSV(el.managementStrategy), // Risk Management Strategy
        sanitizeForCSV(el.managementNote), // Risk Management Note
      ];
    }),
  ];
};

type DownloadCsv = (params: {
  assessment: { id: string; name: string };
  scenarios: AssessmentScenario[];
  assessmentVariables: Array<{ id?: number | null; name?: string | null; value?: number | null; currency: boolean; isInUse: string[]; workspaceVariable?: boolean | null; alias?: string | null; isGlobal?: boolean | null }>;
  workspaceVariables: Variable[];
  onDone: () => void;
}) => void;

export const downloadCsv: DownloadCsv = ({ assessment, scenarios, assessmentVariables: rawAssessmentVars, workspaceVariables, onDone }) => {
  // Merge workspace variables with assessment-specific variables
  // Assessment variables take precedence (they might override workspace values)
  const assessmentVars: Variable[] = (rawAssessmentVars || []).map((v) => ({
    id: v.id!,
    name: v.name!,
    value: v.value!,
    currency: v.currency,
    isInUse: v.isInUse as string[],
    workspace_variable: !!v.workspaceVariable,
    alias: v.alias || null,
    isGlobal: !!v.isGlobal,
  }));

  // Create a map for quick lookup - assessment variables override workspace ones with same name
  const variablesMap = new Map<number, Variable>();
  workspaceVariables.forEach((v) => variablesMap.set(v.id, v));
  assessmentVars.forEach((v) => variablesMap.set(v.id, v));

  const allVariables = Array.from(variablesMap.values());

  const rows = getCSVData(scenarios || [], allVariables);
  const csvString = Papa.unparse(rows, {
    header: true,
  });
  const anchor = document.createElement('a');
  anchor.href = 'data:attachment/csv;charset=utf-8,' + encodeURIComponent(csvString);
  anchor.target = '_blank';
  anchor.download = `${assessment.name}.csv`;
  anchor.innerHTML = 'test';
  gaEventEXPORT('EXPORT_CSV');

  auditEvent({
    action: 'ExportCsv',
    resource: 'assessment',
    assessment_id: assessment.id,
  }).then(onDone);

  anchor.click();
};

export interface VariantsScenario extends Omit<Scenario, 'expected_loss'> {
  expected_loss: {
    base: number;
    live: number;
    projected: number;
  };
}
